import styled from 'styled-components/macro';

import Button from 'components/Button/Button';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';

type TrStyledT = {
  isViewed: boolean;
  isCritical: boolean;
};

export const ListViewStyled = styled.div`
  background-color: ${({ theme }): string => theme.colors.backgroundDefault};
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${({ theme }): string => `
    ${theme.devices.tablet} {
      border-radius: ${theme.radius.md};
    }
  `}
`;

export const HeaderStyled = styled(FlexContainer)`
  padding: 0.75rem 0 0.75rem 0;
  border-bottom: ${({ theme }): string => `${theme.borders.sm} ${theme.colors.primary.variant6}`};
  overflow-x: auto;
  white-space: nowrap;
  ${({ theme }): string => `
    ${theme.devices.smallDesktop} {
      padding: 1.25rem 0;
    }
  `}
`;

export const ContentStyled = styled.div`
  height: 100%;
  min-height: 30vh;
  display: flex;
  flex-direction: column;
  padding: 0 0.75rem 4.625rem;
  overflow-x: hidden;

  ${({ theme }): string => `
    ${theme.devices.smallDesktop} {
      padding: 0 0.25rem 1.625rem;
    }
  `}

  /* Tablet and mobile: no left gutter; scroll happens inside TicketsTable for sticky headers */
  @media screen and (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
    overflow-x: hidden;
  }
  @media screen and (max-width: 424px) {
    padding-left: 0;
    padding-right: 0;
    overflow-x: hidden;
  }
`;

export const TableContainerStyled = styled.div`
  position: relative;
  overflow-x: auto;
  overflow-y: auto;
  height: 100%;
  -webkit-overflow-scrolling: touch;
`;

export const TableStyled = styled.table`
  border-collapse: collapse;
  width: 100%;
  height: 100%;
  max-height: 100%;

  ${({ theme }): string => `
    ${theme.devices.desktop} {
      table-layout: auto;
    }
  `}
  tbody tr {
    cursor: pointer;

    &:hover {
      background-color: ${({ theme }): string => theme.colors.primary.variant3};
    }

    &:first-child {
      td {
        padding-top: 0;
      }
    }

    td {
      ${({ theme }): string => `
        ${theme.devices.desktop} {
          padding-top: 14px;
        }

        ${theme.devices.largeDesktop} {
          padding-top: 0;
        }
      `}
    }
  }

  th,
  td {
    text-align: left;

    &:first-child {
      padding-left: 6px;
      border-radius: ${({ theme }): string => `${theme.radius.sm} 0 0 ${theme.radius.sm}`};
      white-space: pre;
    }

    &:last-child {
      width: auto;
      padding-right: 6px;
      border-radius: ${({ theme }): string => `0 ${theme.radius.sm} ${theme.radius.sm} 0`};
    }

    &:not(:last-child) {
      padding-right: 4px;

      ${({ theme }): string => `
        ${theme.devices.tablet} {
          padding-right: 14px;
        }
      `}
    }
  }

  th {
    padding-bottom: 14px;
    p {
      white-space: nowrap;
    }
  }

  td {
    height: 50px;
  }

  ${({ theme }): string => `
    ${theme.devices.mobile} {
      th,
      td {
        &:not(:last-child) {
          padding-right: 16px;
        }
      }
    }
  `}

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      td {
        height: 68px;
      }
      th {
        word-break: keep-all;
      }
    }
  `}
`;

export const TrStyled = styled.tr<TrStyledT>`
  background-color: ${({ isViewed, theme }): string =>
    !isViewed ? theme.colors.primary.variant14 : ''};
  border-bottom: 2px solid ${({ theme }): string => theme.colors.primary.variant6};
  border: ${({ isCritical, theme }): string =>
    isCritical ? `solid ${theme.colors.secondary.variant7}` : ''};
  &:hover {
    background-color: ${({ isViewed, theme }): string =>
      !isViewed ? `${theme.colors.primary.variant14} !important` : ''};
  }
`;

export const TitleButtonStyled = styled(Button)`
  display: flex;
  align-items: center;
  padding: 0;
`;

export const TextTitleStyled = styled(Typography)`
  color: ${({ theme }): string => theme.colors.primary.variant7};
`;

export const SortingIconsWrapStyled = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 6px;
`;

export const ApprovedStyled = styled.div`
  ${({ theme }): string => `
    ${theme.devices.tablet} {
      margin-left: 20px; 
    }
  `}
`;

export const HasAttachmentStyled = styled.div`
  ${({ theme }): string => `
    ${theme.devices.tablet} {
      margin-left: 20px; 
    }
  `}
`;

export const BreakWordsContentStyled = styled.div`
  ${({ theme }): string => `
    ${theme.devices.desktop} {
      word-break:  break-word; 
    }
  `}
`;

export const UnviewedAsteriskStyled = styled.div`
  color: ${({ theme }): string => theme.colors.highlight.variant3};
  font-weight: bold;
  font-size: ${({ theme }): string => theme.fontSize.sm};
  line-height: 0;

  svg {
    width: 12px;
  }
`;

export const CenteredTdStyled = styled.div`
  text-align: center;
`;

export const LargeCheckBoxInput = styled.input.attrs({ type: 'checkbox' })`
  transform: scale(1.25);
  margin: 0 4px;
`;

export const FormStyled = styled.form`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;
